import { useCallback, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { PaymentBatchesFilterInput } from "#graphql/supergraphTypes";
import { MoneyType } from "../../Money/types";
import { paymentBatchGQLFactory } from "./Factories/paymentBatchGQLFactory";
import { exportPaymentBatchesToCsv } from "./paymentBatchesCsvExport";
import { PaymentBatchSortModel } from "./util/tableFilterUtils";

type ColumnState = ReturnType<AgGrid.GridApi["getColumnState"]>[number];

/**
 * Source of the current grid filter/sort state — typically the live AG-Grid
 * {@link AgGrid.GridApi}, but kept structural so tests can inject a fake.
 */
export type CsvExportGridState = Pick<AgGrid.GridApi, "getFilterModel" | "getColumnState">;

export type UsePaymentBatchesCsvExportResult = {
  /** Trigger the CSV export against the current table filter/sort. */
  exportToCsv: () => Promise<void>;
  /** True while a CSV export is in flight; use to disable the trigger button. */
  isExporting: boolean;
};

/**
 * Translate an AG-Grid column state array (as produced by `gridApi.getColumnState()`)
 * into the {@link PaymentBatchSortModel} shape the factory consumes. Columns with
 * no `sort` are filtered out and the array is ordered by `sortIndex` when present.
 */
const toSortModel = (columnState: ColumnState[]): PaymentBatchSortModel[] =>
  columnState
    .filter((c): c is ColumnState & { sort: string } => Boolean(c.sort))
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map((c) => ({ colId: c.colId, sort: c.sort }));

/**
 * Read the current filter and sort from the supplied grid state and return the
 * `PaymentBatchesFilterInput` payload the export query expects. `amountRef` is
 * forwarded so the `paymentAmount` filter is converted to minor units using the
 * same precision the table uses.
 */
const buildExportFilter = ({
  grid,
  amountRef,
  isGPX,
  isM3Enabled,
}: {
  grid: CsvExportGridState;
  amountRef?: MoneyType;
  isGPX: boolean;
  isM3Enabled: boolean;
}): PaymentBatchesFilterInput => {
  const filterModel = grid.getFilterModel();
  const sortModel = toSortModel(grid.getColumnState());
  return paymentBatchGQLFactory.toInputFilterVariables(
    filterModel,
    sortModel,
    amountRef,
    isGPX,
    isM3Enabled,
  );
};

/**
 * Hook that exposes a CSV export action for the payment batches table. The
 * caller supplies a `getGridState` accessor (typically a ref to the live
 * {@link AgGrid.GridApi}) so the hook can honor the active filters/sort at the
 * moment the user clicks Export.
 *
 * `amountRefPreserver` is the table's stable holder for the live amount
 * reference. The hook reads `.amountRef` at click-time; without it,
 * `paymentAmount` filter values fall back to a precision of 2, which can desync
 * the export from the table's amount-range filter.
 */
export const usePaymentBatchesCsvExport = ({
  getGridState,
  amountRefPreserver,
  isGPX,
  isM3Enabled = false,
}: {
  getGridState: () => CsvExportGridState | null;
  amountRefPreserver?: { readonly amountRef?: MoneyType };
  isGPX: boolean;
  isM3Enabled?: boolean;
}): UsePaymentBatchesCsvExportResult => {
  const client = useApolloClient();
  const [isExporting, setIsExporting] = useState(false);

  const exportToCsv = useCallback(async () => {
    const grid = getGridState();
    if (!grid || isExporting) return;
    setIsExporting(true);
    try {
      const amountRef = amountRefPreserver?.amountRef;
      const inputFilter = buildExportFilter({ grid, amountRef, isGPX, isM3Enabled });
      const rowCount = await exportPaymentBatchesToCsv({ client, inputFilter, isGPX });
      if (rowCount === 0) {
        toast.info("No payments to export for the current filters.");
      }
    } catch (e) {
      addError(formatError(e));
      toast.error("Failed to export payments. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [amountRefPreserver, client, getGridState, isExporting, isGPX, isM3Enabled]);

  return { exportToCsv, isExporting };
};
